import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { getDefaultRouteForRole } from './config/role-routes';

import { LoginPage } from './pages/LoginPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { InternalDashboard } from './pages/dashboard/InternalDashboard';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { AdvancesPage } from './pages/advances/AdvancesPage';
import { AdvanceLoggerPage } from './pages/advances/AdvanceLoggerPage';
import { AdvanceDetailPage } from './pages/advances/AdvanceDetailPage';
import { OfflinePage } from './pages/OfflinePage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ChangeOrdersPage } from './pages/changes/ChangeOrdersPage';
import { ChangeOrderFormPage } from './pages/changes/ChangeOrderFormPage';
import { ChangeOrderDetailPage } from './pages/changes/ChangeOrderDetailPage';
import { DocumentsPage } from './pages/documents/DocumentsPage';
import { MessagesPage } from './pages/messages/MessagesPage';
import { ChatPage } from './pages/messages/ChatPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { FinancePage } from './pages/finance/FinancePage';
import { TokensPage } from './pages/tokens/TokensPage';
import { PermitsPage } from './pages/permits/PermitsPage';
import { CatalogsPage } from './pages/catalogs/CatalogsPage';
import { UsersPage } from './pages/users/UsersPage';
import { ManualCapturePage } from './pages/manual-capture/ManualCapturePage';
import { BIDashboardPage } from './pages/bi/BIDashboardPage';
import { SystemSettingsPage } from './pages/settings/SystemSettingsPage';
import { InternalPage } from './pages/internal/InternalPage';
import { BalancesPage } from './pages/balances/BalancesPage';

function RootRedirect() {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/offline" element={<OfflinePage />} />

      <Route path="/" element={<RootRedirect />} />

      <Route
        path="/gestion/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Dashboard">
              <InternalDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/proyectos"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Proyectos">
              <ProjectsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/proyectos/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Detalle de Proyecto">
              <ProjectDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/avances"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Avances de Obra">
              <AdvancesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/avance-obra"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Registrar Avance">
              <AdvanceLoggerPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/avances/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Detalle de Avance">
              <AdvanceDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/ordenes-cambio"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Órdenes de Cambio">
              <ChangeOrdersPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/ordenes-cambio/nueva"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Nueva Orden de Cambio">
              <ChangeOrderFormPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/ordenes-cambio/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Detalle de Orden">
              <ChangeOrderDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/documentos"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Documentos">
              <DocumentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/mensajes"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Mensajes">
              <MessagesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/mensajes/:projectId"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Chat">
              <ChatPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/configuracion"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Configuración">
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFoundPage />} />

            <Route
        path="/gestion/clientes"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Clientes">
              <ClientsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/finanzas"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Finanzas">
              <FinancePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/tokens"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Tokens">
              <TokensPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/permisos"
        element={
          <ProtectedRoute>
            <DashboardLayout title="Permisos y Trámites">
              <PermitsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/usuarios"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager']}>
            <DashboardLayout title="Usuarios">
              <UsersPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/catalogos"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager', 'data_entry']}>
            <DashboardLayout title="Catálogos">
              <CatalogsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />


      <Route
        path="/gestion/captura-manual"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager', 'data_entry']}>
            <DashboardLayout title="Captura Manual">
              <ManualCapturePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      /> 

      <Route
        path="/gestion/bi"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager']}>
            <DashboardLayout title="Business Intelligence">
              <BIDashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/configuracion"
        element={
          <ProtectedRoute allowedRoles={['sys_admin']}>
            <DashboardLayout title="Configuración del Sistema">
              <SystemSettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/interno"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager', 'accountant', 'receptionist']}>
            <DashboardLayout title="Interno">
              <InternalPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gestion/saldos"
        element={
          <ProtectedRoute allowedRoles={['sys_admin', 'general_manager', 'accountant']}>
            <DashboardLayout title="Saldos">
              <BalancesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      /> 

    </Routes>
  );
}

export default function App() {
  return <AppRoutes />;
}